import React from 'react';
import { useSetAtom } from 'jotai';
import { useIntl } from 'react-intl';
import PropTypes from 'prop-types';
import { saveAs } from 'file-saver';
import { Box, Typography, Stack, IconButton, Tooltip } from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';
import InsertDriveFileRoundedIcon from '@mui/icons-material/InsertDriveFileRounded';
import ImageRoundedIcon from '@mui/icons-material/ImageRounded';
import PictureAsPdfRoundedIcon from '@mui/icons-material/PictureAsPdfRounded';
import ReplyIcon from '@mui/icons-material/Reply';
import FilePaper from './FilePaper';
import useAPI from '../../../api';
import { openSnackbarAdviseAtom } from '../../../Atoms';
import ContentBodyBox from '../../../Components/ContentBodyBox';
import { CenteredFlexBox } from '../../../Components/FlexBox';
import MessageFeedback from '../../../Components/Button/MessageFeedback';
// import FeedbackButtons from '../../../Components/Button/FeedbackButtons';

const selectExtIcon = (fileName) => {
  const arrayFromName = fileName.split('.'); //creates an array filled by elements before and after the . char [filename, extension]
  const extension = arrayFromName.pop(); //removes the first element of array (in this case filename)

  let icon = <InsertDriveFileRoundedIcon sx={{ color: '#579ff3' }} />;

  if (extension === 'jpg' || extension === 'png') {
    icon = <ImageRoundedIcon sx={{ color: '#ffb2e1' }} />;
  } else if (extension === 'pdf') {
    icon = <PictureAsPdfRoundedIcon sx={{ color: 'rgba(205, 0, 0, 0.6)' }} />;
  }

  return icon;
};

const openInNewTab = (url) => {
  const newWindow = window.open(url, '_blank', 'noopener,noreferrer');
  if (newWindow) newWindow.opener = null;
};
const EmailDetailsWidget = function EmailDetailsWidget({
  emailData = null,
  handleReply,
}) {
  const intl = useIntl();
  const API = useAPI();
  const setOpenSnackbarAdvise = useSetAtom(openSnackbarAdviseAtom);

  console.log(emailData);
  const handleDownloadFile = (fileName, method) => {
    const payload = emailData?.attachments?.find(
      ({ name }) => name === fileName
    )?.attachmentId;
    if (payload) {
      API.downloadFile(
        payload,
        (res) => {
          res
            .blob()
            .then((blob) => {
              const newBlob = new Blob([blob], {
                type: res.headers.get('content-type'),
              });
              if (method === 'download') saveAs(newBlob, fileName);
              else {
                const url = URL.createObjectURL(newBlob);
                openInNewTab(url);
              }
            })
            .catch(() => {
              setOpenSnackbarAdvise({
                message: intl.messages.DOWNLOAD_ERROR,
                severity: 'error',
              });
            });
        },
        () => {
          setOpenSnackbarAdvise({
            message: intl.messages.DOWNLOAD_ERROR,
            severity: 'error',
          });
        }
      );
    }
  };

  return (
    <Box
      sx={{
        boxSizing: "border-box",
        display: "flex",
        flexDirection: "column",
        width: "100%",
        height: "100%",
        gap: "16px"
      }}>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between"
        }}>
        <Typography variant="h6">{`${intl.messages.FROM}: ${emailData?.from}`}</Typography>
        <Tooltip title={intl.messages.REPLY} placement="bottom">
          <IconButton onClick={() => handleReply(emailData?.id)}>
            <ReplyIcon />
          </IconButton>
        </Tooltip>
      </Box>
      <Typography variant="body2">{`${intl.messages.TO}: ${emailData?.to}`}</Typography>
      <div
        style={{
          display: 'flex',
          flexDirection: 'row',
          alignItems: 'center',
          gap: '8px',
        }}
      >
        <Typography variant="body2">{`Cc: `}</Typography>
        {emailData?.cc ? (
          emailData.cc?.map((x) => (
            <Typography key={x} variant="body2">
              {x}
            </Typography>
          ))
        ) : (
          <Typography variant="body2">{intl.messages.UNAVAILABLE}</Typography>
        )}
      </div>
      <div
        style={{
          display: 'flex',
          flexDirection: 'row',
          alignItems: 'center',
          gap: '8px',
        }}
      >
        <Typography variant="body2">{`Bcc: `}</Typography>
        {emailData?.bcc ? (
          emailData.bcc?.map((x) => (
            <Typography key={x} variant="body2">
              {x}
            </Typography>
          ))
        ) : (
          <Typography variant="body2">{intl.messages.UNAVAILABLE}</Typography>
        )}
      </div>
      {!emailData?.outbound && (
        <CenteredFlexBox width="100%" justifyContent="flex-end" gap="24px">
          <Typography variant="body1">
            {intl.messages.EMAIL_FEEDBACK}
          </Typography>
          <MessageFeedback
            id={emailData?.id}
            activityTypeName="EMAIL"
            detailsData={{
              completedByFullName: emailData?.completedByFullName,
              completedOn: emailData?.completedOn,
              campaignAdvancement: emailData?.campaignAdvancement ?? 0,
              outcome: emailData?.outcome,
            }}
          />
        </CenteredFlexBox>
      )}
      <ContentBodyBox
        content={emailData?.body}
        styles={{
          box: {
            backgroundColor: 'transparent',
          },
          content: {
            fontFamily: 'Roboto',
          },
        }}
      />
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "flexStart"
        }}>
        <Stack
          spacing={2}
          direction="row"
          sx={{ alignItems: 'center', overflowX: 'auto', padding: '16px' }}
        >
          {emailData?.attachments?.map((file) => (
            <FilePaper
              type={file.type}
              onClickIcon={<DownloadIcon />}
              key={file.name}
              fileName={file.name}
              extensionIcon={selectExtIcon(file.name)}
              onClick={handleDownloadFile}
            />
          ))}
        </Stack>
      </Box>
    </Box>
  );
};

EmailDetailsWidget.propTypes = {
  emailData: PropTypes.object,
  handleReply: PropTypes.func.isRequired,
};

export default EmailDetailsWidget;
